import React from "react";
import "symbol-observable";
import ReactDOM from "react-dom/client";
import "./style.scss";
import * as Sentry from "@sentry/react";
import { split, HttpLink, ApolloLink } from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  FetchPolicy,
} from "@apollo/client";

import Loading from "./components/TPMS/Loading";
import { ApolloProvider } from "@apollo/react-hooks";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { onError } from "@apollo/client/link/error";
import { createClient } from "graphql-ws";
import { RootState, store } from "./state";
import { Provider, useSelector } from "react-redux";
import { setWsConnectedState } from "./state/adminReducer";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { BACKEND_SERVER_URL, BACKEND_WEBSOCKET_URL } from "./config";
import runTimeVars from "./runTimeVars";
import versionData from "./version.json";
import LoginPage from "./pages/LoginPage";
import { routes } from "./routes/routes";
import { MsalProvider } from "@azure/msal-react";
import toast, { Toaster } from "react-hot-toast";
import {
  PublicClientApplication,
  EventType,
  AccountInfo,
} from "@azure/msal-browser";
import { msalConfig } from "./authConfig";
import { v4 as uuidv4 } from "uuid";
import GlobalDataManager from "./components/DataManagers/GlobalDataManager";
import { setRole, setUsername } from "./state/permissionsReducer";

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const enableMsalAuth = runTimeVars.enableMsalAuth !== "false";
let msalInstance: PublicClientApplication | null = null;
if (enableMsalAuth) {
  msalInstance = new PublicClientApplication(msalConfig);
} else {
  store.dispatch(setUsername("Local User"));
  store.dispatch(setRole(runTimeVars.noAuthRole));
}

if (process.env.NODE_ENV === "production") {
  const environmentName: string | undefined =
    runTimeVars.environmentName === "undefined"
      ? "unknown"
      : runTimeVars.environmentName;

  Sentry.init({
    dsn: "https://e4cd72910aeafdabb27762e5e076c411@o4504327957512192.ingest.us.sentry.io/4508450537930752",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    environment: environmentName,
    release: versionData.version,
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

export const connectionId = uuidv4();

function WebSocketClient() {
  return createClient({
    url: `${BACKEND_WEBSOCKET_URL}/api/graphql`,
    connectionParams: async () => {
      if (!msalInstance) {
        return {};
      }
      const account = msalInstance.getActiveAccount();
      const tokenResponse = await msalInstance.acquireTokenSilent({
        scopes: [
          "api://c522907c-9bd2-44ca-8d25-3adf77294104/Token.Verification",
        ],
        account: account || undefined,
      });
      return {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        ConnectionId: connectionId,
      };
    },
    retryAttempts: Infinity,
    shouldRetry: () => true,
    retryWait: async (): Promise<void> => {
      await new Promise((resolve) => setTimeout(resolve, 3000));
    },
    lazy: true,
  });
}

let wsClient = WebSocketClient();

wsClient.on("connecting", () => {
  console.log("WEBSOCKET ATTEMPTING CONNECTION....");
});

wsClient.on("connected", () => {
  store.dispatch(setWsConnectedState(true));
  console.log("WEBSOCKET CONNECTED");
  // Refetch all active queries on reconnect to sync state
  client.refetchQueries({ include: "active" });
});

wsClient.on("closed", () => {
  store.dispatch(setWsConnectedState(false));
  console.log("WEBSOCKET DISCONNECTED");
  wsClient = WebSocketClient();
});

const httpLink = new HttpLink({
  uri: `${BACKEND_SERVER_URL}/api/graphql`,
  headers: {},
});

const authLink = setContext(async (_, { headers }) => {
  if (!msalInstance) {
    return {
      headers: {
        ...headers,
      },
    };
  }
  // Get MSAL instance and account
  const account = msalInstance.getActiveAccount();
  // Acquire token
  const tokenResponse = await msalInstance.acquireTokenSilent({
    scopes: ["api://c522907c-9bd2-44ca-8d25-3adf77294104/Token.Verification"],
    account: account || undefined,
  });
  const accessToken = tokenResponse.accessToken;
  return {
    headers: {
      ...headers,
      Authorization: accessToken ? `Bearer ${accessToken}` : "",
    },
  };
});

const wsLink = new GraphQLWsLink(wsClient);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink,
);

const retryLink = new RetryLink({
  delay: {
    initial: 3000, // Initial delay between retries (in milliseconds)
    max: 3000, // Maximum delay between retries (in milliseconds)
    jitter: false, // Apply jitter to the delays (randomize them slightly)
  },
  attempts: {
    max: Infinity, // Maximum number of retry attempts
    retryIf: (error, operation) => {
      if (operation.getContext().skipRetry === true) {
        return false;
      }
      // Determine whether to retry based on the error and operation
      // Return true to retry, false otherwise
      return !!error;
    },
  },
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (err.extensions?.code === "FORBIDDEN") {
        toast.error("You do not have permission to perform this action.", {
          duration: 2000,
        });
      }
    }
  }
});

const link = ApolloLink.from([authLink, errorLink, retryLink, splitLink]);

const client = new ApolloClient<NormalizedCacheObject>({
  link: link,
  cache: new InMemoryCache({
    addTypename: false,
  }),
  defaultOptions: {
    query: {
      fetchPolicy: "network-only" as FetchPolicy,
      notifyOnNetworkStatusChange: true,
    },
    watchQuery: {
      fetchPolicy: "network-only" as FetchPolicy,
      notifyOnNetworkStatusChange: true,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const AuthenticatedContent = () => {
  const role = useSelector((state: RootState) => state.permissions.role);

  const permissions = useSelector(
    (state: RootState) => state.permissions.permissions,
  );

  if (!permissions || Object.keys(permissions).length === 0) {
    console.log("Waiting for permissions...");
    return (
      <ApolloProvider client={client}>
        <GlobalDataManager role={role} />
        <div className="loadingContainer">
          <Loading />
        </div>
      </ApolloProvider>
    );
  }

  const getRouteElement = (route: (typeof routes)[keyof typeof routes]) => {
    if (!route.requiresPermission) {
      return <route.component />;
    }

    if (!route.permissionGroup || route.permissionBit === null) {
      return <Navigate to="/403" replace />;
    }

    const hasPermission =
      ((permissions?.[route.permissionGroup] ?? 0) >> route.permissionBit) & 1;

    return hasPermission ? <route.component /> : <Navigate to="/403" replace />;
  };

  return (
    <ApolloProvider client={client}>
      <Toaster
        toastOptions={{
          className: "",
          style: {
            fontWeight: "bold",
          },
        }}
      />
      <GlobalDataManager role={role} />

      <BrowserRouter>
        <Routes>
          {Object.values(routes).map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={getRouteElement(route)}
            />
          ))}
        </Routes>
      </BrowserRouter>
    </ApolloProvider>
  );
};

const UnauthenticatedContent = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />}></Route>
      </Routes>
    </BrowserRouter>
  );
};

const Content = () => {
  if (!msalInstance) {
    return <AuthenticatedContent />;
  }

  const account = msalInstance.getActiveAccount();

  const isAuthenticated = !!account;
  console.log(
    "MSAL Authentication Enabled:",
    enableMsalAuth,
    "User Authenticated:",
    isAuthenticated,
  );
  if (!isAuthenticated && enableMsalAuth !== false) {
    console.log("User not authenticated");
    if (window.location.pathname !== "/login") {
      console.log("Redirecting to login page...");
      window.location.replace("/login");
    } else {
      return <UnauthenticatedContent />;
    }
  } else {
    console.log("User authenticated, rendering app...");
    return <AuthenticatedContent />;
  }
};

// Initialize MSAL before rendering the app
if (msalInstance) {
  msalInstance
    .initialize()
    .then(() => {
      // Default to using the first account if no account is active on page load
      if (
        !msalInstance.getActiveAccount() &&
        msalInstance.getAllAccounts().length > 0
      ) {
        const account = msalInstance.getAllAccounts()[0];
        msalInstance.setActiveAccount(account);
      }

      const account = msalInstance.getActiveAccount();
      const username = account?.name;
      const role = account?.idTokenClaims?.roles?.[0] || "";
      store.dispatch(setUsername(username));
      store.dispatch(setRole(role));
      Sentry.setUser({ username: username });

      // Listen for sign-in event and set active account
      msalInstance.addEventCallback((event) => {
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
          console.log("Login successful, setting active account...");
          const account = event.payload as AccountInfo;
          msalInstance.setActiveAccount(account);
          const username = account?.name;
          const role = account?.idTokenClaims?.roles?.[0] || "";
          store.dispatch(setUsername(username));
          store.dispatch(setRole(role));
          Sentry.setUser({ username: username });
          window.location.replace("/");
        }
      });

      // Now render the app after MSAL is initialized
      root.render(
        <MsalProvider instance={msalInstance}>
          <Provider store={store}>
            <Content />
          </Provider>
        </MsalProvider>,
      );
    })
    .catch((error) => {
      console.error("MSAL initialization failed:", error);
    });
} else {
  // If MSAL authentication is not enabled, render the app immediately
  root.render(
    <Provider store={store}>
      <Content />
    </Provider>,
  );
}
